import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, ZoomIn, ZoomOut, Trash2, MapPin } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Select from '../../components/Select';
import ConfirmDialog from '../../components/ConfirmDialog';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { loadPlanImageElement } from '../../utils/planImage';
import { EQUIPMENT_COLORS, EQUIPMENT_ICONS, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';

const PlaceEquipment = () => {
  const { error: toastError } = useToast();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const canvasRef = useRef(null);
  const planBlobUrlRef = useRef(null);
  const [client, setClient] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planImage, setPlanImage] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedPlacements, setSelectedPlacements] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [draggedPlacement, setDraggedPlacement] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, count: 0 });

  const planIdParam = searchParams.get('planId');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when client or ?planId= changes
  }, [clientId, planIdParam]);

  useEffect(() => {
    return () => {
      if (planBlobUrlRef.current) {
        URL.revokeObjectURL(planBlobUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (planImage) {
      drawCanvas();
    }
  }, [planImage, placements, zoom, pan, selectedPlacements]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientData, plansData, equipmentData] = await Promise.all([
        apiService.getClient(clientId),
        apiService.getClientPlans(clientId),
        apiService.getEquipment(clientId),
      ]);

      setClient(clientData);
      setPlans(plansData);
      setEquipment(equipmentData);

      // Select plan from URL parameter or default plan
      const planIdFromUrl = searchParams.get('planId');
      let planToLoad = null;

      if (planIdFromUrl) {
        planToLoad = plansData.find(p => p.id === parseInt(planIdFromUrl));
      }
      
      if (!planToLoad) {
        // Find default plan or first plan
        planToLoad = plansData.find(p => p.isDefault === 1) || plansData[0];
      }

      if (planToLoad) {
        setSelectedPlan(planToLoad);
        loadPlanImage(planToLoad);
        await loadPlacements(planToLoad.id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlacements = async (planId) => {
    try {
      const placementsData = await apiService.getPlacements(planId);
      setPlacements(placementsData);
    } catch (error) {
      console.error('Failed to load placements:', error);
      setPlacements([]);
    }
  };

  const loadPlanImage = async (plan) => {
    if (!plan?.fileUrl && !plan?.fileName) return;

    try {
      if (planBlobUrlRef.current) {
        URL.revokeObjectURL(planBlobUrlRef.current);
        planBlobUrlRef.current = null;
      }

      const { img, blobUrl } = await loadPlanImageElement(plan);
      planBlobUrlRef.current = blobUrl;
      setPlanImage(img);
    } catch (error) {
      console.error('Failed to load plan image:', error);
      toastError(error.message || 'Impossible de charger l\'image du plan');
    }
  };

  const handlePlanChange = async (e) => {
    const planId = parseInt(e.target.value);
    const plan = plans.find(p => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
      setPlanImage(null); // Clear current image
      setPlacements([]); // Clear current placements
      loadPlanImage(plan);
      await loadPlacements(planId);
      // Update URL
      setSearchParams({ planId: planId.toString() });
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !planImage) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Apply transformations
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw plan image
    const aspectRatio = planImage.width / planImage.height;
    const drawWidth = width / zoom;
    const drawHeight = drawWidth / aspectRatio;
    ctx.drawImage(planImage, 0, 0, drawWidth, drawHeight);

    // Draw placement markers
    placements.forEach((placement) => {
      const x = placement.positionX * drawWidth;
      const y = placement.positionY * drawHeight;
      const isDragging = draggedPlacement?.id === placement.id;
      const isSelected = selectedPlacements.some(p => p.id === placement.id);

        // Draw marker
        ctx.beginPath();
      ctx.arc(x, y, isDragging || isSelected ? 12 : 8, 0, 2 * Math.PI);
      ctx.fillStyle = EQUIPMENT_COLORS[placement.type] || '#ff0000';
        ctx.fill();
      ctx.strokeStyle = isSelected ? '#ffff00' : (isDragging ? '#000' : '#fff');
      ctx.lineWidth = isDragging || isSelected ? 3 : 2;
        ctx.stroke();

        // Draw label
        ctx.fillStyle = '#000';
        ctx.font = 'bold 12px Arial';
      ctx.fillText(placement.serialNumber, x + 15, y + 5);
    });

    ctx.restore();
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Scale factors between displayed size and actual canvas pixel size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Convert click position from displayed size to actual canvas pixels
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;
    
    const x = (clickX - pan.x) / zoom;
    const y = (clickY - pan.y) / zoom;

    const drawWidth = canvas.width / zoom;
    const drawHeight = (drawWidth * planImage.height) / planImage.width;

    return { x, y, drawWidth, drawHeight };
  };

  const findPlacementAtPosition = (x, y, drawWidth, drawHeight) => {
    // Check if click is on an existing placement (within 15px radius)
    for (const placement of placements) {
      const placementX = placement.positionX * drawWidth;
      const placementY = placement.positionY * drawHeight;
      const distance = Math.sqrt(Math.pow(x - placementX, 2) + Math.pow(y - placementY, 2));
        if (distance <= 15) {
        return placement;
      }
    }
    return null;
  };

  const handleMouseDown = (e) => {
    if (!planImage || !selectedPlan) return;

    const { x, y, drawWidth, drawHeight } = getCanvasCoordinates(e);
    const clickedPlacement = findPlacementAtPosition(x, y, drawWidth, drawHeight);

    if (clickedPlacement) {
      const isAlreadySelected = selectedPlacements.some(p => p.id === clickedPlacement.id);
      
      if (e.ctrlKey || e.metaKey) {
        // Multi-select with Ctrl/Cmd
        if (isAlreadySelected) {
          setSelectedPlacements(selectedPlacements.filter(p => p.id !== clickedPlacement.id));
        } else {
          setSelectedPlacements([...selectedPlacements, clickedPlacement]);
        }
      } else {
        // Single select or start dragging
        if (!isAlreadySelected) {
          setSelectedPlacements([clickedPlacement]);
        }
      setDragging(true);
        setDraggedPlacement(clickedPlacement);
      }
    } else if (selectedEquipment) {
      // Create new placement locally (instant, smooth)
      const relativeX = x / drawWidth;
      const relativeY = y / drawHeight;

      const newPlacement = {
        id: `temp_${Date.now()}_${Math.random()}`, // Temporary ID
        equipmentId: selectedEquipment.id,
        planId: selectedPlan.id,
        positionX: relativeX,
        positionY: relativeY,
        serialNumber: selectedEquipment.serialNumber,
        type: selectedEquipment.type,
      };

      setPlacements([...placements, newPlacement]);
      setSelectedPlacements([]); // Deselect when placing new
    } else {
      // Clicked on empty space - deselect all
      setSelectedPlacements([]);
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging || !draggedPlacement || !planImage) return;

    const { x, y, drawWidth, drawHeight } = getCanvasCoordinates(e);

    // Convert to relative position (0-1)
    const relativeX = Math.max(0, Math.min(1, x / drawWidth));
    const relativeY = Math.max(0, Math.min(1, y / drawHeight));

    // Update placement position locally (smooth, instant)
    const updatedPlacements = placements.map((placement) =>
      placement.id === draggedPlacement.id
        ? { ...placement, positionX: relativeX, positionY: relativeY }
        : placement
    );

    setPlacements(updatedPlacements);
  };

  const handleMouseUp = () => {
    setDragging(false);
    setDraggedPlacement(null);
  };

  const handleDeleteSelected = () => {
    if (selectedPlacements.length === 0) return;
    
    // Open confirmation dialog
    setConfirmDialog({ open: true, count: selectedPlacements.length });
  };

  const confirmDelete = () => {
    // Get IDs of selected placements
    const selectedIds = selectedPlacements.map(p => p.id);
    
    // Remove from local state (instant, smooth)
    setPlacements(placements.filter(p => !selectedIds.includes(p.id)));
    
    // Clear selection
    setSelectedPlacements([]);
  };

  const handleSave = async () => {
    if (!selectedPlan) return;

    try {
      setSaving(true);
      
      // Fetch ALL existing placements from the backend (not from local state)
      const allExistingPlacements = await apiService.getPlacements(selectedPlan.id);
      
      // Delete ALL existing placements for this plan
      for (const placement of allExistingPlacements) {
        await apiService.deletePlacement(placement.id);
      }

      // Create all placements from current local state
      for (const placement of placements) {
        await apiService.createPlacement({
          equipmentId: placement.equipmentId,
          planId: selectedPlan.id,
          positionX: placement.positionX,
          positionY: placement.positionY,
        });
        }

      navigate('/plans');
    } catch (error) {
      console.error('Failed to save placements:', error);
      toastError('Erreur lors de la sauvegarde des emplacements');
    } finally {
      setSaving(false);
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="outline"
            icon={ArrowLeft}
            onClick={() => navigate('/plans')}
            className="mb-4"
          >
            Retour
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Placer les Équipements</h1>
          <p className="text-gray-600 mt-1">Client: {client?.name}</p>
        </div>
        <Button variant="primary" icon={Save} onClick={handleSave} loading={saving}>
          Enregistrer
        </Button>
      </div>

      {/* Plan Selector */}
      {plans.length > 1 && (
        <Card>
          <div className="flex items-center space-x-4">
            <MapPin className="w-5 h-5 text-red-600" />
            <div className="flex-1">
              <Select
                label="Plan à utiliser"
                value={selectedPlan?.id || ''}
                onChange={handlePlanChange}
              >
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                    {plan.location && ` - ${plan.location}`}
                    {plan.floor && ` (${plan.floor})`}
                    {plan.isDefault === 1 && ' ⭐'}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      )}

      {!selectedPlan && plans.length === 0 && (
        <Card>
          <div className="text-center py-8">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun plan disponible pour ce client.</p>
            <p className="text-sm text-gray-500 mt-2">
              Veuillez d'abord télécharger un plan depuis la page de gestion des plans.
            </p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Equipment List */}
        <Card className="lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Équipements</h2>
          <div className="space-y-2">
            {equipment.map((item) => {
              const placementCount = placements.filter(p => p.equipmentId === item.id).length;
              return (
              <button
                key={item.id}
                onClick={() => setSelectedEquipment(item)}
                className={`w-full p-3 rounded-lg text-left transition-all ${
                  selectedEquipment?.id === item.id
                    ? 'bg-primary-50 border-2 border-primary-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{EQUIPMENT_ICONS[item.type]}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.serialNumber}
                    </p>
                    <p className="text-xs text-gray-500">{EQUIPMENT_TYPE_LABELS[item.type] || item.type}</p>
                  </div>
                    {placementCount > 0 && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        {placementCount}
                      </span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        </Card>

        {/* Canvas */}
        <Card className="lg:col-span-3">
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {selectedPlacements.length > 0 ? (
                  <span>
                    <strong>{selectedPlacements.length}</strong> emplacement{selectedPlacements.length > 1 ? 's' : ''} sélectionné{selectedPlacements.length > 1 ? 's' : ''}
                  </span>
                ) : selectedEquipment ? (
                  <span>
                    Équipement sélectionné: <strong>{selectedEquipment.serialNumber}</strong>
                  </span>
                ) : (
                  <span>Sélectionnez un équipement pour le placer</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {selectedPlacements.length > 0 && (
                  <Button 
                    variant="danger" 
                    size="sm" 
                    icon={Trash2}
                    onClick={handleDeleteSelected}
                  >
                    Supprimer
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleZoomOut}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="sm" onClick={handleZoomIn}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Canvas */}
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                className={`w-full ${dragging ? 'cursor-grabbing' : 'cursor-crosshair'}`}
              />
            </div>

            {/* Instructions */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Instructions:</strong> Sélectionnez un équipement dans la liste, puis cliquez sur le plan pour le placer (vous pouvez placer le même équipement plusieurs fois). Cliquez et déplacez un marqueur pour le repositionner. 
                <br />
                <strong>Sélection:</strong> Cliquez sur un marqueur pour le sélectionner (bordure jaune). Maintenez <kbd className="px-2 py-1 bg-white border border-blue-300 rounded text-xs">Ctrl</kbd> (ou <kbd className="px-2 py-1 bg-white border border-blue-300 rounded text-xs">Cmd</kbd> sur Mac) pour sélectionner plusieurs marqueurs.
                <br />
                <strong>Supprimer:</strong> Sélectionnez un ou plusieurs emplacements, puis cliquez sur le bouton "Supprimer".
                <br />
                N'oubliez pas de cliquer sur "Enregistrer" pour sauvegarder tous les changements.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, count: 0 })}
        onConfirm={confirmDelete}
        title="Supprimer les emplacements"
        message={
          confirmDialog.count === 1
            ? "Êtes-vous sûr de vouloir supprimer cet emplacement ?"
            : `Êtes-vous sûr de vouloir supprimer ces ${confirmDialog.count} emplacements ?`
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
      />
    </div>
  );
};

export default PlaceEquipment;
